import { IEditorV3, isIEditorV3 } from "@azu-business/editor-v3";
import {
  AibBlockLine,
  GridItemTrafficLight,
  isAioExternalSingle,
  isGridItemTrafficLight,
} from "aic";
import { AioExternalSingleWithSource } from "contexts/hierarchy/interface";
import { CommentReadDto } from "../comments/interface";
import { isCommentReadDto } from "../comments/isCommentReadDto";
import { eTlfOutputType } from "../enums";
import { isTlfItemIndexReadDto, TlfItemIndexReadDto } from "./TlfItemIndexReadDto";

export interface TitlesFootnotesReadDto extends TlfItemIndexReadDto {
  programName?: string;
  suffix?: string;
  outputName?: string;
  outputType?: eTlfOutputType;
  titles: AibBlockLine<IEditorV3>[];
  footnotes: AibBlockLine<IEditorV3>[];
  singles: AioExternalSingleWithSource<IEditorV3>[];
  changedTitles?: GridItemTrafficLight[];
  shellMinorVersion: number;
  comments: CommentReadDto[];
}

/**
 * Type guard that asserts whether a value conforms to the TitlesFootnotesReadDto shape.
 *
 * @param obj - The object to check
 * @returns boolean
 */
export const isTitlesFootnotesReadDto = (obj: unknown): obj is TitlesFootnotesReadDto => {
  // Check object
  if (typeof obj !== "object" || obj === null) return false;
  // Check lower interface
  if (!isTlfItemIndexReadDto(obj)) return false;
  // Check properties exist
  if (!("titles" in obj) || !("footnotes" in obj) || !("shellMinorVersion" in obj)) return false;

  // Create dummy check object
  const chkObj = obj as TitlesFootnotesReadDto;

  // Check basic props
  if (chkObj.programName !== undefined && typeof chkObj.programName !== "string") return false;
  if (chkObj.suffix !== undefined && typeof chkObj.suffix !== "string") return false;
  if (chkObj.outputName !== undefined && typeof chkObj.outputName !== "string") return false;
  if (chkObj.outputType !== undefined && !Object.values(eTlfOutputType).includes(chkObj.outputType))
    return false;
  if (chkObj.shellMinorVersion !== undefined && typeof chkObj.shellMinorVersion !== "number")
    return false;

  // Check arrays
  if (!Array.isArray(chkObj.titles) || !Array.isArray(chkObj.footnotes)) return false;
  if (!Array.isArray(chkObj.comments) || chkObj.comments.some((x) => !isCommentReadDto(x)))
    return false;
  if (!Array.isArray(chkObj.changedTitles) && chkObj.changedTitles !== undefined) return false;

  // Check array contents
  if (chkObj.changedTitles?.some((x) => !isGridItemTrafficLight(x))) return false;
  if (
    !Array.isArray(chkObj.singles) ||
    chkObj.singles.some((o) => !isAioExternalSingle(o, isIEditorV3))
  )
    return false;
  // Everything is good
  return true;
};
